import React, {useState} from 'react';
import {Dropdown, DropdownItem, DropdownMenu, DropdownToggle} from 'reactstrap';
import {Check} from 'lucide-react';
import {useGetAvailableAssistants} from '../api/threadHooks';
import UnpublishedBadge from './UnpublishedBadge';
import SysAdminOnlyBadge from './SysAdminOnlyBadge';

interface AssistantSwitcherProps {
    currentKey: string;
    currentName: string;
    onSelect: (key: string) => void;
}

/**
 * The assistant's name in the panel header, as a picker of every assistant the
 * user may open. Customer users normally have one (the Certavue assistant), so
 * it is plain text; sysadmins also see the platform's own assistants.
 */
const AssistantSwitcher: React.FC<AssistantSwitcherProps> = ({
    currentKey,
    currentName,
    onSelect
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const {data: assistants} = useGetAvailableAssistants();

    if (!assistants || assistants.length < 2) {
        return <span className="fw-bold">{currentName}</span>;
    }

    return (
        <Dropdown isOpen={isOpen} toggle={() => setIsOpen(open => !open)}>
            <DropdownToggle
                caret
                color="link"
                className="fw-bold p-0 text-decoration-none text-body"
                title="Switch assistant"
            >
                {currentName}
            </DropdownToggle>
            <DropdownMenu style={{minWidth: 260, maxWidth: 360}} strategy="fixed">
                {assistants.map(assistant => (
                    <DropdownItem
                        key={assistant.id}
                        onClick={() => {
                            if (assistant.key !== currentKey) {
                                onSelect(assistant.key);
                            }
                        }}
                        className="text-wrap"
                    >
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <span className="fw-medium text-nowrap">{assistant.name}</span>
                            {assistant.sysAdminOnly && <SysAdminOnlyBadge />}
                            {!assistant.isPublished &&
                                !assistant.sysAdminOnly && <UnpublishedBadge />}
                            {assistant.key === currentKey && (
                                <Check size={14} />
                            )}
                        </div>
                        {assistant.description && (
                            <div className="small text-muted">
                                {assistant.description}
                            </div>
                        )}
                    </DropdownItem>
                ))}
            </DropdownMenu>
        </Dropdown>
    );
};

export default AssistantSwitcher;
